import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import { getDecryptedAccessToken } from "../../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";
import ApiServices from "../../../../Utils/ApiServices/ApiServices";
import LocationFormCard from "./Components/LocationFormCard";
import LocationListCard from "./Components/LocationListCard";
import WorkingHoursSection from "./Components/WorkingHoursSection";
import MenuManagementSection from "./Components/MenuManagementSection";
import MenuCsvSection from "./Components/MenuCsvSection";

const defaultLocationForm = {
  name: "",
  address_line1: "",
  street: "",
  postal_code: "",
  city: "",
  state: "",
  country: "",
  contact_number: "",
  google_location_url: "",
  google_location_id: "",
  latitude: "",
  longitude: "",
  is_primary: false,
  is_active: true,
};

const ClientLocations = () => {
  const token = getDecryptedAccessToken();
  const clientId = sessionStorage.getItem("activeClientId");

  const [loading, setLoading] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);

  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [locationForm, setLocationForm] = useState(defaultLocationForm);
  const [locationErrors, setLocationErrors] = useState({});
  const [mainTab, setMainTab] = useState("working_hours");

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const apiHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token]
  );

  const selectedLocation = useMemo(
    () => locations.find((loc) => loc.id === selectedLocationId) || null,
    [locations, selectedLocationId]
  );

  const sortedLocations = useMemo(() => {
    return [...locations].sort((a, b) => {
      if (a.is_primary && !b.is_primary) return -1;
      if (!a.is_primary && b.is_primary) return 1;
      if (a.is_active && !b.is_active) return -1;
      if (!a.is_active && b.is_active) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [locations]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, severity, message });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const mapApiLocationToForm = (location) => ({
    name: location?.name || "",
    address_line1: location?.address_line1 || "",
    street: location?.street || "",
    postal_code: location?.postal_code || "",
    city: location?.city || "",
    state: location?.state || "",
    country: location?.country || "",
    contact_number: location?.contact_number || "",
    google_location_url: location?.google_location_url || "",
    google_location_id: location?.google_location_id || "",
    latitude:
      location?.latitude === null || location?.latitude === undefined
        ? ""
        : String(location.latitude),
    longitude:
      location?.longitude === null || location?.longitude === undefined
        ? ""
        : String(location.longitude),
    is_primary: !!location?.is_primary,
    is_active:
      location?.is_active === undefined ? true : Boolean(location?.is_active),
  });

  const validateUrl = (value) => {
    if (!value) return true;
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  };

  const validateLocationForm = () => {
    const errors = {};

    if (!locationForm.name.trim()) errors.name = "Location name is required";
    if (!locationForm.address_line1.trim()) {
      errors.address_line1 = "Address line 1 is required";
    }
    if (!locationForm.city.trim()) errors.city = "City is required";
    if (!locationForm.state.trim()) errors.state = "State is required";
    if (!locationForm.country.trim()) errors.country = "Country is required";
    if (!locationForm.postal_code.trim()) {
      errors.postal_code = "Postal code is required";
    }

    if (
      locationForm.contact_number &&
      !/^\+?[0-9\s\-()]{7,20}$/.test(locationForm.contact_number)
    ) {
      errors.contact_number = "Enter a valid contact number";
    }

    if (
      locationForm.google_location_url &&
      !validateUrl(locationForm.google_location_url)
    ) {
      errors.google_location_url = "Enter a valid URL";
    }

    if (
      locationForm.latitude !== "" &&
      (isNaN(Number(locationForm.latitude)) ||
        Number(locationForm.latitude) < -90 ||
        Number(locationForm.latitude) > 90)
    ) {
      errors.latitude = "Latitude must be between -90 and 90";
    }

    if (
      locationForm.longitude !== "" &&
      (isNaN(Number(locationForm.longitude)) ||
        Number(locationForm.longitude) < -180 ||
        Number(locationForm.longitude) > 180)
    ) {
      errors.longitude = "Longitude must be between -180 and 180";
    }

    setLocationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetLocationForm = () => {
    setLocationForm(defaultLocationForm);
    setLocationErrors({});
    setEditingLocationId(null);
  };

  const fetchLocations = async () => {
    if (!clientId || !token) return;

    setLoading(true);
    try {
      const response = await ApiServices.get(
        `/api/v1/clients/${clientId}/locations/`,
        apiHeaders
      );
      const data = response?.data || response || [];
      const list = Array.isArray(data) ? data : [];

      setLocations(list);

      if (list.length > 0) {
        const preferred =
          list.find((loc) => loc.is_primary) ||
          [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""))[0];
        setSelectedLocationId((prev) => prev || preferred?.id || "");
      } else {
        setSelectedLocationId("");
      }
    } catch (error) {
      showSnackbar(
        error?.response?.data?.detail || "Failed to load locations",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, [clientId, token]);

  const handleLocationInputChange = (field, value) => {
    setLocationForm((prev) => ({ ...prev, [field]: value }));
    setLocationErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleEditLocation = (location) => {
    setEditingLocationId(location.id);
    setLocationForm(mapApiLocationToForm(location));
    setLocationErrors({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSaveLocation = async () => {
    if (!validateLocationForm()) {
      showSnackbar("Please fix location form errors", "error");
      return;
    }

    const payload = {
      ...locationForm,
      latitude: locationForm.latitude === "" ? null : Number(locationForm.latitude),
      longitude:
        locationForm.longitude === "" ? null : Number(locationForm.longitude),
    };

    setSavingLocation(true);
    try {
      if (editingLocationId) {
        await ApiServices.put(
          `/api/v1/clients/${clientId}/locations/${editingLocationId}/`,
          payload,
          apiHeaders
        );
        showSnackbar("Location updated successfully");
      } else {
        await ApiServices.post(
          `/api/v1/clients/${clientId}/locations/`,
          payload,
          apiHeaders
        );
        showSnackbar("Location created successfully");
      }

      resetLocationForm();
      await fetchLocations();
    } catch (error) {
      showSnackbar(
        error?.response?.data?.detail || "Failed to save location",
        "error"
      );
    } finally {
      setSavingLocation(false);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    const confirmed = window.confirm("Are you sure you want to delete this location?");
    if (!confirmed) return;

    try {
      await ApiServices.delete(`/api/v1/clients/${clientId}/locations/${locationId}/`, {
        Authorization: `Bearer ${token}`,
      });
      showSnackbar("Location deleted successfully");
      if (selectedLocationId === locationId) setSelectedLocationId("");
      if (editingLocationId === locationId) resetLocationForm();
      await fetchLocations();
    } catch (error) {
      showSnackbar(
        error?.response?.data?.detail || "Failed to delete location",
        "error"
      );
    }
  };

  const handleSetPrimary = async (locationId) => {
    try {
      await ApiServices.post(
        `/api/v1/clients/${clientId}/locations/${locationId}/set-primary/`,
        {},
        { Authorization: `Bearer ${token}` }
      );
      showSnackbar("Primary location updated successfully");
      await fetchLocations();
    } catch (error) {
      showSnackbar(
        error?.response?.data?.detail || "Failed to set primary location",
        "error"
      );
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" fontWeight={700}>
            Client Locations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage location details, working hours, and menu from one place.
          </Typography>
        </Grid>

        <Grid item xs={12} md={5}>
          <LocationFormCard
            locationForm={locationForm}
            locationErrors={locationErrors}
            editingLocationId={editingLocationId}
            savingLocation={savingLocation}
            onChange={handleLocationInputChange}
            onSave={handleSaveLocation}
            onReset={resetLocationForm}
            onRefresh={fetchLocations}
          />
        </Grid>

        <Grid item xs={12} md={7}>
          <LocationListCard
            loading={loading}
            locations={sortedLocations}
            selectedLocationId={selectedLocationId}
            onSelect={setSelectedLocationId}
            onEdit={handleEditLocation}
            onDelete={handleDeleteLocation}
            onSetPrimary={handleSetPrimary}
          />
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Tabs
                value={mainTab}
                onChange={(_, value) => setMainTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2 }}
              >
                <Tab value="working_hours" label="Working Hours" />
                <Tab value="menu" label="Menu Management" />
                <Tab value="menu_csv" label="Menu CSV" />
              </Tabs>

              {mainTab === "working_hours" && (
                <WorkingHoursSection
                  token={token}
                  clientId={clientId}
                  selectedLocation={selectedLocation}
                  locations={sortedLocations}
                  selectedLocationId={selectedLocationId}
                  onSelectLocation={setSelectedLocationId}
                  showSnackbar={showSnackbar}
                />
              )}

              {mainTab === "menu" && (
                <MenuManagementSection
                  token={token}
                  clientId={clientId}
                  selectedLocation={selectedLocation}
                  locations={sortedLocations}
                  selectedLocationId={selectedLocationId}
                  onSelectLocation={setSelectedLocationId}
                  showSnackbar={showSnackbar}
                />
              )}

              {mainTab === "menu_csv" && (
                <MenuCsvSection
                  token={token}
                  clientId={clientId}
                  selectedLocation={selectedLocation}
                  locations={sortedLocations}
                  selectedLocationId={selectedLocationId}
                  onSelectLocation={setSelectedLocationId}
                  showSnackbar={showSnackbar}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={closeSnackbar} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>

      {loading && !locations.length ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : null}
    </Box>
  );
};

export default ClientLocations;